import * as React from 'react';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {Link} from 'shells/link/link';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';
import {PaginatedTable} from 'shells/table/paginated-table';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {GetMoneyPageTriggers_maUrlTriggers_edges_node as MoneyPageTrigger} from 'nutshell-graphql-types';

import {
    GET_MONEY_PAGE_TRIGGERS_LIMIT,
    useGetMoneyPageTriggers,
} from './graphql/hooks/use-get-money-page-triggers';

import styles from './page-activity.module.css';

type Props = {
    moneyPageId: string;
};

export function PageActivity(props: Props) {
    const {triggers, isLoading, error, totalCount, fetchNextPage, fetchPreviousPage} =
        useGetMoneyPageTriggers(props.moneyPageId);

    const columns = React.useMemo(
        () => [
            {
                id: 'contact',
                Header: 'Visitor',
                accessor: (trigger: MoneyPageTrigger) => trigger.contact,
                dataType: 'component',
                minWidth: 200,
                Cell: ({cell}: {cell: {value: MoneyPageTrigger['contact']}}) => {
                    const contact = cell.value;

                    if (!contact) {
                        return <span className='color-light'>Unknown contact</span>;
                    }

                    return (
                        <LinkedEntity
                            name={contact.name}
                            htmlUrlPath={contact.htmlUrlPath}
                            type='contacts'
                        />
                    );
                },
                disableSortBy: true,
            },
            {
                id: 'lead',
                Header: 'Open lead',
                accessor: (trigger: MoneyPageTrigger) => trigger.lead,
                dataType: 'component',
                minWidth: 200,
                Cell: ({cell}: {cell: {value: MoneyPageTrigger['lead']}}) => {
                    const lead = cell.value;

                    if (!lead) {
                        return <span className='color-light'>No open leads</span>;
                    }

                    const assigneeName =
                        lead.assignee && lead.assignee.name ? lead.assignee.name : null;

                    return (
                        <div>
                            <LinkedEntity
                                name={lead.name}
                                htmlUrlPath={lead.htmlUrlPath}
                                type='leads'
                                greenLeads={true}
                            />
                            {assigneeName && <div className='color-light'>{assigneeName}</div>}
                        </div>
                    );
                },
                disableSortBy: true,
            },
            {
                id: 'triggeredTime',
                Header: 'Triggered',
                accessor: (trigger: MoneyPageTrigger) => trigger.triggeredTime,
                dataType: 'component',
                minWidth: 150,
                Cell: ({cell}: {cell: {value: number}}) => (
                    <RelativeTimestamp datetime={cell.value} />
                ),
                disableSortBy: true,
            },
        ],
        []
    );

    return (
        <div className={styles['engagement-table']}>
            <PaginatedTable
                // @ts-expect-error - react-table column types
                columns={columns}
                data={triggers}
                totalCount={totalCount}
                pageSize={GET_MONEY_PAGE_TRIGGERS_LIMIT}
                fetchNextPage={fetchNextPage}
                fetchPreviousPage={fetchPreviousPage}
                isLoading={isLoading}
                error={error || undefined}
                fullWidth={true}
                disableVirtualization={true}
                emptyState={
                    <NothingFoundEmptyState
                        title='No identified visitors yet!'
                        subtitle={
                            <>
                                Visitors who submitted a form or clicked an email link will
                                automatically trigger your automation when they reach this
                                page.&nbsp;
                                <Link
                                    href='https://support.nutshell.com/en/articles/13460946-marketing-marketing-automations'
                                    newTab={true}
                                    variant='primary'
                                >
                                    Learn more
                                </Link>
                            </>
                        }
                        icon={
                            <img
                                src='/include/images/marketing-automations/activity-log.png'
                                alt=''
                            />
                        }
                    />
                }
            />
        </div>
    );
}
